// 로그인 후 1회 모달 — 「환자 정보 처리위탁 확인 — 한 곳에서 확인하기」 (2026-10-08 대표 "권고대로", 법무검토 §3 ☆ B·C, P1-2)
//   대상: 승인된 치과 계정(MainLayout 안 — 승인 전은 App.js 게이트가 막음). 대표 = 남은 문서 확인 / 직원 = ☆ C 안내만.
//   남은 문서 = 현재 판 미확인(미확인·판 바뀜·철회) + 웹 원문이 서버 현재 판·해시와 같은 것. 이미 확인한 문서는 "이미 확인함" 줄.
//   [나중에] 필수(P1-2 — 기존 회원에게 강제 금지). 같은 남은 문서 묶음(판 포함)으로는 한 번만 띄운다(utils/clinicDelegation wasPrompted).
//   가입 직후 서버 기록이 실패한 계정(SIGNUP_FAILED_KEY)은 서명과 무관하게 띄운다(조용한 실패 금지).
//   suspended = 약관 v7.0 제4조의7 재확인 모달이 떠 있는 동안 — 제4조의7 먼저, 부속 2개 뒤(법무검토 §2 ④).
import React, { useEffect, useMemo, useState } from 'react';
import ClinicDelegationDocBox from './ClinicDelegationDocBox';
import {
    useClinicDelegationStatus, pendingItems, acceptClinicDelegationDocs, delegationCheckLabel, delegationFootnote,
    MODAL_TITLE, MODAL_BODY, MODAL_LATER_NOTE, STAFF_TEXT, BOX_MARK_HINT, confirmedLine,
    promptSignature, wasPrompted, markPrompted,
} from '../../utils/clinicDelegation';

function ClinicDelegationPrompt({ uid, isStaff, enabled, suspended }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const { loading, items, reload } = useClinicDelegationStatus(!!enabled && !!uid);
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);
    const [shown, setShown] = useState(false);   // 이 화면 수명 동안 1번만(저장소가 막혀도 되풀이하지 않게)

    // 대표: 확인 가능한 남은 문서 / 직원: 현재 판 확인이 안 된 문서(조회 오류 제외)
    const pending = useMemo(() => (isStaff ? items.filter(it => !it.done && !it.err) : pendingItems(items)), [items, isStaff]);
    const doneItems = useMemo(() => items.filter(it => it.done), [items]);
    const sig = useMemo(() => `${isStaff ? 'staff' : 'owner'}|${promptSignature(pending)}`, [pending, isStaff]);

    useEffect(() => {
        if (!enabled || !uid || loading || suspended || open || shown) return;
        if (pending.length === 0) return;
        if (wasPrompted(uid, sig)) return;
        setShown(true);
        setOpen(true);
    }, [enabled, uid, loading, suspended, open, shown, pending.length, sig]);

    if (!open) return null;

    const close = () => { markPrompted(uid, sig); setOpen(false); setChecked(false); setErrors([]); };

    const confirm = async () => {
        if (busy || !checked || isStaff) return;
        setBusy(true); setErrors([]);
        try {
            const res = await acceptClinicDelegationDocs(pending);
            const fails = res.filter(r => !r.ok);
            const after = await reload(true);
            if (fails.length === 0) {
                markPrompted(uid, `owner|${promptSignature(pendingItems(after))}`);
                setOpen(false); setChecked(false);
            } else {
                setErrors(fails.map(f => f.error));
                setChecked(false);
            }
        } finally { setBusy(false); }
    };

    const keys = pending.map(it => it.key);
    return (
        <div style={S.backdrop} role="dialog" aria-modal="true" aria-label={MODAL_TITLE}>
            <div style={S.modal}>
                <div style={S.title}>{MODAL_TITLE}</div>
                <div style={S.body}>{MODAL_BODY}</div>
                {doneItems.map(it => (
                    <div key={it.key} style={S.okLine}>{confirmedLine(it)}</div>
                ))}
                {isStaff ? (
                    <>
                        <div style={S.staff}>{STAFF_TEXT}</div>
                        <div style={S.btnRow}>
                            <button type="button" onClick={close} style={S.btnP}>닫기</button>
                        </div>
                    </>
                ) : (
                    <>
                        {pending.map((it, i) => <ClinicDelegationDocBox key={it.key} no={i + 1} doc={it} />)}
                        <div style={S.hint}>{BOX_MARK_HINT}</div>
                        <label style={{ ...S.agree, ...(checked ? S.agreeOn : null) }}>
                            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
                                style={{ width: 20, height: 20, flexShrink: 0, margin: 0, accentColor: '#0f6b5c' }} />
                            <span style={{ fontWeight: 700 }}>{`${delegationCheckLabel(pending)} (필수)`}</span>
                        </label>
                        <div style={S.foot}>{delegationFootnote(keys)}</div>
                        {errors.map((m, i) => <div key={i} style={S.errNote}>{m}</div>)}
                        <div style={S.btnRow}>
                            <button type="button" disabled={busy} onClick={close} style={S.btnS}>나중에</button>
                            <button type="button" disabled={!checked || busy} onClick={confirm} style={{ ...S.btnP, opacity: !checked || busy ? 0.5 : 1 }}>
                                {busy ? '기록 중…' : '확인'}
                            </button>
                        </div>
                        <div style={S.later}>{MODAL_LATER_NOTE}</div>
                    </>
                )}
            </div>
        </div>
    );
}

const S = {
    backdrop: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', padding: '20px 22px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
    title: { fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 8 },
    body: { fontSize: 13, color: '#475569', lineHeight: 1.7 },
    okLine: { marginTop: 8, fontSize: 12.5, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '7px 11px' },
    staff: { marginTop: 12, fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', lineHeight: 1.6 },
    hint: { marginTop: 10, fontSize: 12, color: '#64748b', lineHeight: 1.5 },
    agree: { display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, color: '#0f172a', marginTop: 6, cursor: 'pointer', background: '#f0fdf9', border: '2px solid #0f6b5c', borderRadius: 10, padding: '11px 14px', lineHeight: 1.55 },
    agreeOn: { background: '#d1fae5' },
    foot: { marginTop: 8, fontSize: 12, color: '#64748b', lineHeight: 1.6 },
    errNote: { marginTop: 8, fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px', lineHeight: 1.55 },
    btnRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 },
    btnP: { padding: '9px 18px', background: '#0f6b5c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' },
    btnS: { padding: '9px 18px', background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' },
    later: { marginTop: 8, fontSize: 12, color: '#94a3b8', textAlign: 'right' },
};

export default ClinicDelegationPrompt;
